import { useState } from "react";
import { Input } from "@/components/ui/input";

const PARTNER_TYPES = [
  {
    code: "NETWORK",
    label: "Network Partner",
    description:
      "Books orders, manages Revenue partners, accountable for service quality",
    ring: "ring-blue-200 bg-blue-50 text-blue-800 border-blue-300",
  },
  {
    code: "REVENUE",
    label: "Revenue Partner",
    description: "Self drop/pickup, linked under a Network partner",
    ring: "ring-purple-200 bg-purple-50 text-purple-800 border-purple-300",
  },
  {
    code: "EXECUTION",
    label: "Execution Partner",
    description: "Pickup and delivery only — auto drivers and fleet owners",
    ring: "ring-emerald-200 bg-emerald-50 text-emerald-800 border-emerald-300",
  },
];

const BUSINESS_TYPES = [
  "Proprietorship",
  "Partnership",
  "Private Limited",
  "LLP",
  "Individual / Freelancer",
];

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}Z[0-9A-Z]{1}$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

export const validateStep1 = (formData) => {
  const errors = {};
  if (!formData.partner_type) errors.partner_type = "Required";
  if (!formData.business_name?.trim()) errors.business_name = "Required";
  if (!formData.owner_name?.trim()) errors.owner_name = "Required";
  if (!formData.business_type) errors.business_type = "Required";
  if (!formData.pan?.trim()) errors.pan = "Required";
  else if (!PAN_REGEX.test(formData.pan.toUpperCase()))
    errors.pan = "Invalid PAN (5 letters + 4 digits + 1 letter)";
  if (formData.gstin && !GSTIN_REGEX.test(formData.gstin.toUpperCase()))
    errors.gstin = "Invalid GSTIN (15 alphanumeric)";
  return errors;
};

export default function Step1Business({ formData, setFormData, errors }) {
  const [touched, setTouched] = useState({});
  const set = (k, v) => setFormData((p) => ({ ...p, [k]: v }));
  const onBlur = (k) => setTouched((t) => ({ ...t, [k]: true }));

  const activeType = PARTNER_TYPES.find((t) => t.code === formData.partner_type);

  const fieldError = (k) => errors[k] || "";

  return (
    <div className="space-y-6" data-testid="step1-business">
      <div>
        <h2 className="text-lg font-bold text-slate-800">Business Information</h2>
        <p className="text-sm text-slate-500">Tell us about the business entity</p>
      </div>

      {/* Partner Type */}
      <div>
        <Label required>Partner Type</Label>
        <div className="grid grid-cols-3 gap-2 mt-1.5">
          {PARTNER_TYPES.map((t) => (
            <button
              key={t.code}
              type="button"
              onClick={() => set("partner_type", t.code)}
              data-testid={`partner-type-${t.code.toLowerCase()}`}
              className={`border rounded-lg px-3 py-2 text-sm font-semibold transition ${
                formData.partner_type === t.code
                  ? `${t.ring} ring-2`
                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {activeType && (
          <div className={`mt-2 rounded-md border px-3 py-2 text-xs ${activeType.ring}`}>
            <span className="font-semibold">{activeType.code}: </span>
            {activeType.description}
          </div>
        )}
        <Err msg={fieldError("partner_type")} />
      </div>

      {/* Two col fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field
          label="Business Name"
          required
          error={fieldError("business_name")}
          testid="business-name"
        >
          <Input
            value={formData.business_name || ""}
            onChange={(e) => set("business_name", e.target.value)}
            onBlur={() => onBlur("business_name")}
            placeholder="Acme Logistics Pvt Ltd"
            data-testid="business-name-input"
          />
        </Field>

        <Field label="Owner Name" required error={fieldError("owner_name")} testid="owner-name">
          <Input
            value={formData.owner_name || ""}
            onChange={(e) => set("owner_name", e.target.value)}
            onBlur={() => onBlur("owner_name")}
            placeholder="Full name as on PAN"
            data-testid="owner-name-input"
          />
        </Field>

        <Field
          label="Business Type"
          required
          error={fieldError("business_type")}
          testid="business-type"
        >
          <select
            value={formData.business_type || ""}
            onChange={(e) => set("business_type", e.target.value)}
            className="w-full border border-slate-300 rounded h-10 px-2 text-sm bg-white"
            data-testid="business-type-select"
          >
            <option value="">Select…</option>
            {BUSINESS_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="GSTIN"
          helper="Leave blank if not GST registered"
          error={fieldError("gstin")}
          testid="gstin"
        >
          <Input
            value={formData.gstin || ""}
            onChange={(e) => set("gstin", e.target.value.toUpperCase())}
            onBlur={() => onBlur("gstin")}
            placeholder="37AABCS1234M1Z5"
            maxLength={15}
            data-testid="gstin-input"
          />
        </Field>

        <Field label="PAN" required error={fieldError("pan")} testid="pan">
          <Input
            value={formData.pan || ""}
            onChange={(e) => set("pan", e.target.value.toUpperCase())}
            onBlur={() => onBlur("pan")}
            placeholder="AABCS1234M"
            maxLength={10}
            data-testid="pan-input"
          />
        </Field>

        <Field label="MSME Number" testid="msme">
          <Input
            value={formData.msme_number || ""}
            onChange={(e) => set("msme_number", e.target.value)}
            placeholder="UDYAM-XX-00-0000000"
            data-testid="msme-input"
          />
        </Field>

        <Field label="Years in Business" testid="years">
          <Input
            type="number"
            min="0"
            value={formData.years_in_business ?? ""}
            onChange={(e) =>
              set(
                "years_in_business",
                e.target.value === "" ? "" : Number(e.target.value)
              )
            }
            placeholder="5"
            data-testid="years-input"
          />
        </Field>
      </div>
    </div>
  );
}

function Label({ children, required }) {
  return (
    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide">
      {children} {required && <span className="text-red-500">*</span>}
    </label>
  );
}

function Field({ label, required, helper, error, children, testid }) {
  return (
    <div data-testid={`field-${testid}`}>
      <Label required={required}>{label}</Label>
      <div className="mt-1.5">{children}</div>
      {helper && !error && (
        <p className="text-[11px] text-slate-400 mt-1">{helper}</p>
      )}
      <Err msg={error} />
    </div>
  );
}

function Err({ msg }) {
  if (!msg) return null;
  return <p className="text-[11px] text-red-600 mt-1">{msg}</p>;
}
